import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, Send, CheckCircle2, XCircle, Globe, Clock } from 'lucide-react';
import { useTenancy } from '@/contexts/TenancyContext';
import { deiterateAPI } from '@/js/external-js/variableModules/utils/deiterateAPI.util';

interface AIQueryResponse {
  success: boolean;
  message: string;
  provider: string;
  processingTime: number;
  sources?: Array<{
    title: string;
    url: string;
    snippet: string;
  }>;
  error?: string;
}

interface ProviderConfig {
  provider: string;
  localUrl: string;
  hasOpenAIKey: boolean;
  hasAnthropicKey: boolean;
}

const AITestPage: React.FC = () => {
  const { activeTenancy } = useTenancy();
  const tenancyId = activeTenancy?.id;
  const [prompt, setPrompt] = useState('');
  const [context, setContext] = useState('');
  const [useWebSearch, setUseWebSearch] = useState(false);
  const [loading, setLoading] = useState(false);
  const [response, setResponse] = useState<AIQueryResponse | null>(null);
  const [config, setConfig] = useState<ProviderConfig | null>(null);
  const [configLoading, setConfigLoading] = useState(false);

  const loadConfig = async () => {
    setConfigLoading(true);
    try {
      const result = await deiterateAPI.GET('/ai/config');
      // The result is the config object directly
      setConfig(result);
    } catch (error: any) {
      console.error('Failed to load config:', error);
    } finally {
      setConfigLoading(false);
    }
  };

  React.useEffect(() => {
    loadConfig();
  }, []);

  const handleSubmit = async () => {
    if (!prompt.trim() || !tenancyId) return;

    setLoading(true);
    setResponse(null);

    try {
      const result = await deiterateAPI.POST('/ai/query', {
        body: JSON.stringify({
          prompt: prompt.trim(),
          context: context.trim() || undefined,
          useWebSearch,
          customerId: tenancyId
        })
      });

      // The result is the response object directly
      setResponse(result);
    } catch (error: any) {
      setResponse({
        success: false,
        message: '',
        provider: config?.provider || 'unknown',
        processingTime: 0,
        error: error.message || 'Request failed'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      handleSubmit();
    }
  };

  return (
    <div className="container mx-auto p-6 max-w-6xl">
      <div className="mb-6">
        <h1 className="text-3xl font-bold mb-2">AI Query Test Page</h1>
        <p className="text-muted-foreground">
          Test AI queries with different providers and web search capabilities
        </p>
      </div>

      {/* Provider Configuration */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            Provider Configuration
            {configLoading && <Loader2 className="h-4 w-4 animate-spin" />}
          </CardTitle>
          <CardDescription>Current AI provider settings</CardDescription>
        </CardHeader>
        <CardContent>
          {config ? (
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Label className="w-32">Provider:</Label>
                <Badge variant="outline" className="font-mono">
                  {config.provider}
                </Badge>
              </div>
              {config.provider === 'local' && (
                <div className="flex items-center gap-2">
                  <Label className="w-32">Local URL:</Label>
                  <code className="text-sm bg-muted px-2 py-1 rounded">
                    {config.localUrl}
                  </code>
                </div>
              )}
              <div className="flex items-center gap-2">
                <Label className="w-32">OpenAI Key:</Label>
                <Badge variant={config.hasOpenAIKey ? 'default' : 'secondary'}>
                  {config.hasOpenAIKey ? 'Configured' : 'Not Set'}
                </Badge>
              </div>
              <div className="flex items-center gap-2">
                <Label className="w-32">Anthropic Key:</Label>
                <Badge variant={config.hasAnthropicKey ? 'default' : 'secondary'}>
                  {config.hasAnthropicKey ? 'Configured' : 'Not Set'}
                </Badge>
              </div>
            </div>
          ) : (
            <p className="text-muted-foreground">Loading configuration...</p>
          )}
        </CardContent>
      </Card>

      {/* Query Input */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Query Input</CardTitle>
          <CardDescription>
            Enter your prompt and optional context. Press Cmd/Ctrl+Enter to submit.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="prompt">Prompt *</Label>
            <Textarea
              id="prompt"
              placeholder="What are the key requirements for ISO 27001 compliance?"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              onKeyDown={handleKeyPress}
              rows={4}
              className="resize-none"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="context">Additional Context (optional)</Label>
            <Textarea
              id="context"
              placeholder="Customer is in healthcare industry..."
              value={context}
              onChange={(e) => setContext(e.target.value)}
              onKeyDown={handleKeyPress}
              rows={3}
              className="resize-none"
            />
          </div>

          <div className="flex items-center space-x-2">
            <Switch
              id="web-search"
              checked={useWebSearch}
              onCheckedChange={setUseWebSearch}
            />
            <Label htmlFor="web-search" className="flex items-center gap-2 cursor-pointer">
              <Globe className="h-4 w-4" />
              Enable Web Search
            </Label>
          </div>

          <Button
            onClick={handleSubmit}
            disabled={loading || !prompt.trim() || !tenancyId}
            className="w-full"
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Processing...
              </>
            ) : (
              <>
                <Send className="mr-2 h-4 w-4" />
                Send Query
              </>
            )}
          </Button>
        </CardContent>
      </Card>

      {/* Response */}
      {response && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              Response
              {response.success ? (
                <CheckCircle2 className="h-5 w-5 text-green-500" />
              ) : (
                <XCircle className="h-5 w-5 text-red-500" />
              )}
            </CardTitle>
            <CardDescription className="flex items-center gap-4">
              <span className="flex items-center gap-1">
                Provider: <Badge variant="outline">{response.provider}</Badge>
              </span>
              <span className="flex items-center gap-1">
                <Clock className="h-3 w-3" />
                {response.processingTime.toFixed(2)}s
              </span>
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {response.success ? (
              <>
                <div className="space-y-2">
                  <Label>AI Response</Label>
                  <div className="bg-muted p-4 rounded-lg whitespace-pre-wrap">
                    {response.message}
                  </div>
                </div>

                {response.sources && response.sources.length > 0 && (
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <Globe className="h-4 w-4" />
                      Sources ({response.sources.length})
                    </Label>
                    <div className="space-y-2">
                      {response.sources.map((source, index) => (
                        <Card key={index} className="bg-muted/50">
                          <CardContent className="p-3">
                            <div className="font-medium">{source.title}</div>
                            {source.url && (
                              <a
                                href={source.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-sm text-primary hover:underline"
                              >
                                {source.url}
                              </a>
                            )}
                            {source.snippet && (
                              <p className="text-sm text-muted-foreground mt-1">
                                {source.snippet}
                              </p>
                            )}
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <Alert variant="destructive">
                <XCircle className="h-4 w-4" />
                <AlertDescription>
                  {response.error || 'An unknown error occurred'}
                </AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>
      )}

      {/* Instructions */}
      <Card className="mt-6">
        <CardHeader>
          <CardTitle>Instructions</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm text-muted-foreground">
          <p>
            <strong>Provider Configuration:</strong> Set via environment variables:
          </p>
          <ul className="list-disc list-inside space-y-1 ml-4">
            <li>
              <code className="bg-muted px-1 rounded">AI_PROVIDER</code> - local, openai, or
              anthropic
            </li>
            <li>
              <code className="bg-muted px-1 rounded">OPENAI_API_KEY</code> - Required for OpenAI
            </li>
            <li>
              <code className="bg-muted px-1 rounded">ANTHROPIC_API_KEY</code> - Required for
              Anthropic
            </li>
          </ul>
          <p className="mt-4">
            <strong>Web Search:</strong> When enabled, the AI can search the web for current
            information. Support varies by provider.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default AITestPage;
